import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
} from '@mui/material';
import {
  AccessTime,
  Schedule,
  CheckCircle,
} from '@mui/icons-material';
import { IAmenidad } from '../../models/reservas';

interface ISeleccionAmenidadProps {
  setAmenidad: (amenidad: number) => void;
  amenidad: number;
  amenidadesList: Array<IAmenidad>;
  previousStep?: () => void;
  nextStep?: () => void;
  previousText?: string;
  nextText?: string;
  reservaAction?: () => void;
}

// Memoizar el componente de tarjeta individual
const AmenidadCard = React.memo(({ 
  amenidadItem, 
  isSelected, 
  onSelect 
}: { 
  amenidadItem: IAmenidad; 
  isSelected: boolean; 
  onSelect: (id: number) => void; 
}) => {
  // Memoizar las funciones de utilidad
  const defaultImage = useMemo(() => {
    const nombreLower = amenidadItem.nombre.toLowerCase();
    if (nombreLower.includes('piscina') || nombreLower.includes('pool')) {
      return '/assets/water-drop.png';
    } else if (nombreLower.includes('gimnasio') || nombreLower.includes('gym')) {
      return '/assets/home-manager.png';
    } else if (nombreLower.includes('sala') || nombreLower.includes('room')) {
      return '/assets/edificio-icono.png';
    } else if (nombreLower.includes('cancha') || nombreLower.includes('court')) {
      return '/assets/calendar.png';
    } else {
      return '/assets/edificio-icono.png';
    }
  }, [amenidadItem.nombre]);

  const gradient = useMemo(() => {
    const nombreLower = amenidadItem.nombre.toLowerCase();
    if (nombreLower.includes('piscina') || nombreLower.includes('pool')) {
      return 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)';
    } else if (nombreLower.includes('gimnasio') || nombreLower.includes('gym')) {
      return 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)';
    } else if (nombreLower.includes('sala') || nombreLower.includes('room')) {
      return 'linear-gradient(135deg, #42a5f5 0%, #2196f3 100%)';
    } else if (nombreLower.includes('cancha') || nombreLower.includes('court')) {
      return 'linear-gradient(135deg, #64b5f6 0%, #42a5f5 100%)';
    } else {
      return 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)';
    }
  }, [amenidadItem.nombre]);

  const handleClick = useCallback(() => {
    onSelect(amenidadItem.servicio);
  }, [amenidadItem.servicio, onSelect]);

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isSelected ? 'scale(1.05) translateY(-10px)' : 'scale(1)',
        borderRadius: '20px',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        border: isSelected
          ? '3px solid #fff'
          : '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: isSelected
          ? '0 25px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1)'
          : '0 8px 32px rgba(0,0,0,0.1)',
        position: 'relative',
        minHeight: '380px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'scale(1.03) translateY(-5px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.2)',
        }
      }}
      onClick={handleClick}
    >
      {/* Indicador de selección */}
      {isSelected && (
        <Box
          position="absolute"
          top={12}
          right={12}
          zIndex={2}
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            borderRadius: '50%',
            p: 0.5,
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
          }}
        >
          <CheckCircle sx={{ color: 'white', fontSize: 24 }} />
        </Box>
      )}

      {/* Badge de precio - solo mostrar si no usa límites */}
      {!amenidadItem.usar_Limites && (
        <Box
          position="absolute"
          top={12}
          left={12}
          zIndex={2}
          sx={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
            borderRadius: '15px',
            px: 1.5,
            py: 0.5,
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'white', 
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          >
            Q{amenidadItem.valor?.toFixed(0)}
          </Typography>
        </Box>
      )}

      <CardMedia
        component="img"
        height="180"
        image={amenidadItem.imagen || defaultImage}
        alt={amenidadItem.nombre}
        sx={{
          objectFit: 'cover',
          background: gradient,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.1)',
            zIndex: 1
          }
        }}
      />
      
      <CardContent sx={{ p: 3, background: 'rgba(255, 255, 255, 0.9)', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {amenidadItem.nombre.includes('/') ? (
          <>
            <Typography 
              variant="h6" 
              component="div" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                color: '#2c3e50',
                fontSize: '1.1rem',
                mb: 1
              }}
            >
              {amenidadItem.nombre.split('/')[0]}
            </Typography>
            <Typography 
              variant="body2" 
              component="div" 
              gutterBottom 
              sx={{ 
                color: '#666',
                fontSize: '0.9rem',
                mb: 2,
                fontStyle: 'italic',
                textAlign: 'justify'
              }}
            >
              {amenidadItem.nombre.split('/').slice(1).join('/')}
            </Typography>
          </>
        ) : (
          <Typography 
            variant="h6" 
            component="div" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              color: '#2c3e50',
              fontSize: '1.1rem',
              mb: 2
            }}
          >
            {amenidadItem.nombre}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 1.5,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white'
          }}>
            <Schedule sx={{ fontSize: 18, mr: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: '500' }}>
              {amenidadItem.inicio_Amenidad}:00 - {amenidadItem.fin_Amenidad}:00
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 1.5,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
            color: 'white'
          }}>
            <AccessTime sx={{ fontSize: 18, mr: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: '500' }}>
              Máx. {amenidadItem.tiempo_Maximo?.toFixed(1)} hrs
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

AmenidadCard.displayName = 'AmenidadCard';

export const SeleccionAmenidad = ({
  setAmenidad,
  amenidad,
  amenidadesList,
  previousStep,
  nextStep,
  previousText,
  nextText,
  reservaAction,
}: ISeleccionAmenidadProps) => {
  const [disabled, setDisabled] = React.useState(amenidad === -1);

  // Memoizar la amenidad seleccionada
  const amenidadSeleccionada = useMemo(() => 
    amenidadesList.find((ameni) => ameni.servicio === Number(amenidad)),
    [amenidadesList, amenidad]
  );

  // Memoizar el handler de selección
  const handleAmenidadSelect = useCallback((amenidadId: number) => {
    if (amenidad === amenidadId) {
      setDisabled(true);
      setAmenidad(-1);
    } else {
      setDisabled(amenidadId === -1);
      setAmenidad(amenidadId);
    }
  }, [amenidad, setAmenidad]);

  // Memoizar los handlers de botones
  const handlePrevious = useCallback(() => {
    previousStep?.();
  }, [previousStep]);

  const handleNext = useCallback(() => {
    nextStep?.();
  }, [nextStep]);

  const handleReserva = useCallback(() => {
    reservaAction?.();
  }, [reservaAction]);

  return (
    <Box 
      width={'90%'} 
      pb={'160px'}
      sx={{
        minHeight: '100vh',
        pt: 0,
        px: 3,
        pb: 3
      }}
    >
      <Typography 
        variant="h4" 
        component="div" 
        gutterBottom 
        marginY='1em' 
        textAlign={'center'}
        sx={{
          color: '#2c3e50',
          fontWeight: 'bold',
          mb: 4
        }}
      >
        Selecciona tu Amenidad 
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {amenidadesList.map((amenidadItem: IAmenidad) => (
          <Grid item xs={12} sm={6} md={4} key={amenidadItem.servicio}>
            <AmenidadCard
              amenidadItem={amenidadItem}
              isSelected={amenidad === amenidadItem.servicio}
              onSelect={handleAmenidadSelect}
            />
          </Grid>
        ))}
      </Grid>

      {/* Espacio adicional al final para scroll */}
      <Box sx={{ height: '100px' }} />

      {/* Botones fijos en la parte inferior */}
      <Box
        position={'fixed'}
        bottom={0}
        left={0}
        right={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '1rem',
          zIndex: 30
        }}
      >
        {/* Indicador de amenidad seleccionada */}
        {!disabled && amenidadSeleccionada && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              p: 1.5,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(25, 118, 210, 0.2)'
            }}
          >
            <CheckCircle sx={{ fontSize: 20, mr: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: '500', fontSize: '0.9rem' }}>
              Seleccionado: {amenidadSeleccionada.nombre} {!amenidadSeleccionada.usar_Limites && `- Q${amenidadSeleccionada.valor?.toFixed(0)}`}
            </Typography>
          </Box>
        )}

        {/* Botones */}
        <Box
          display={'flex'}
          justifyContent={previousStep && nextStep ? 'space-between' : 'center'}
        >
          {previousStep && (
            <Button 
              variant="outlined" 
              sx={{ 
                width: '40%', 
                mx: '0.5rem',
                borderRadius: '15px',
                border: '2px solid #1976d2',
                color: '#1976d2',
                fontWeight: 'bold',
                '&:hover': {
                  background: '#1976d2',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)'
                }
              }} 
              onClick={handlePrevious}
            >
              {previousText}
            </Button>
          )}
          {nextStep && (
            <Button 
              disabled={disabled} 
              variant="contained" 
              sx={{ 
                width: '40%', 
                mx: '0.5rem',
                borderRadius: '15px',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                fontWeight: 'bold',
                boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(25, 118, 210, 0.4)'
                },
                '&:disabled': {
                  background: '#ccc',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }} 
              onClick={handleNext}
            >
              {nextText}
            </Button>
          )}
          {reservaAction && (
            <Button 
              variant="contained" 
              sx={{ 
                width: '40%', 
                mx: '0.5rem',
                borderRadius: '15px',
                background: 'linear-gradient(135deg, #42a5f5 0%, #2196f3 100%)',
                fontWeight: 'bold',
                boxShadow: '0 8px 25px rgba(66, 165, 245, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(66, 165, 245, 0.4)'
                }
              }} 
              onClick={handleReserva}
            >
              Reservar
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};